import { useEffect, useState } from "react";
import { getVisitorId, getSessionId } from "@/hooks/use-page-tracking";

export type Variant = {
  key: string;
  name: string;
  isControl: boolean;
  weight: number;
  config: Record<string, unknown>;
};

export type ActiveExperiment = {
  key: string;
  name: string;
  variants: Variant[];
};

// 32-bit FNV-1a. MUST stay byte-for-byte identical to the server implementation
// in api-server/src/routes/routes.ts so client and server agree on bucketing.
function fnv1a(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function pickVariant(variants: Variant[], visitorId: string, experimentKey: string): Variant {
  const norm = variants.map((v) => ({ v, weight: Math.max(1, Math.round(v.weight || 1)) }));
  const total = norm.reduce((s, x) => s + x.weight, 0);
  let bucket = fnv1a(`${visitorId}:${experimentKey}`) % total;
  for (const x of norm) {
    if (bucket < x.weight) return x.v;
    bucket -= x.weight;
  }
  return norm[norm.length - 1].v;
}

let activeCache: Promise<ActiveExperiment[]> | null = null;
export function fetchActiveExperiments(): Promise<ActiveExperiment[]> {
  if (!activeCache) {
    activeCache = fetch("/api/experiments/active")
      .then((r) => (r.ok ? r.json() : []))
      .catch(() => []);
  }
  return activeCache;
}

const exposed = new Set<string>();
function recordExposure(experimentKey: string, visitorId: string): void {
  if (exposed.has(experimentKey)) return;
  exposed.add(experimentKey);
  try {
    fetch("/api/experiments/expose", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ experimentKey, visitorId, sessionId: getSessionId() }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* no-op */
  }
}

/**
 * Manually fire an exposure for an experiment — use this when the experiment
 * was assigned with `defer = true` in `useExperiment` and you want to record
 * exposure at a meaningful moment (e.g. dates selected) rather than page-render.
 * The internal `exposed` Set dedupes so calling this multiple times is safe.
 */
export function fireExposure(experimentKey: string): void {
  recordExposure(experimentKey, getVisitorId());
}

export async function getVariant(experimentKey: string, options?: { noExpose?: boolean }): Promise<Variant | null> {
  try {
    const all = await fetchActiveExperiments();
    const exp = all.find((e) => e.key === experimentKey);
    if (!exp || exp.variants.length === 0) return null;
    const visitorId = getVisitorId();
    const variant = pickVariant(exp.variants, visitorId, experimentKey);
    if (!options?.noExpose) recordExposure(experimentKey, visitorId);
    return variant;
  } catch {
    return null;
  }
}

/**
 * Record a conversion for the visitor against running experiments. Pass an
 * experimentKey to scope it, or omit to convert every assigned experiment.
 */
export function recordConversion(experimentKey?: string): void {
  try {
    fetch("/api/experiments/convert", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visitorId: getVisitorId(), sessionId: getSessionId(), experimentKey }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* no-op */
  }
}

/**
 * Resolve the assigned variant for an experiment. Returns null until resolved
 * (callers should render the control in that case). Records an exposure once,
 * unless `defer = true` — in which case call `fireExposure(key)` manually at
 * the moment that makes sense (e.g. dates selected, not page render).
 */
export function useExperiment(experimentKey: string, defer = false): { variantKey: string | null; config: Record<string, unknown> } {
  const [state, setState] = useState<{ variantKey: string | null; config: Record<string, unknown> }>({
    variantKey: "control",
    config: {},
  });
  useEffect(() => {
    let mounted = true;
    getVariant(experimentKey, { noExpose: defer }).then((v) => {
      if (mounted && v) setState({ variantKey: v.key, config: v.config || {} });
    });
    return () => { mounted = false; };
  }, [experimentKey, defer]);
  return state;
}
